import { AdminTownListTemplate } from '@/components/templates';
import { useAdminTownList } from '@/hooks/admin';
import { useDownloadTownsCsv } from '@/hooks/admin/useAdminTownCsv';
import { useImportTownsCsv } from '@/hooks/admin/useAdminTownImport';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import { Town, TownFilterParams } from '@/types/admin-town';
import { SorterResult } from 'antd/es/table/interface';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const SiteAdminTownPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();

  // Disable Enter key navigation on this page (used for search inputs)
  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  // Prevent browser from opening file when dropped outside drop zone
  useEffect(() => {
    const prevent = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  const page = searchParams.get('page') || '1';
  const sortBy = searchParams.get('sort_by');
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;
  const searchName = searchParams.get('name');
  const searchPostalCode = searchParams.get('postal_code');

  const breadcrumbItems = [
    {
      title: '郵便番号',
    },
  ];

  const {
    data,
    isLoading,
    isError: listError,
    refetch,
  } = useAdminTownList({
    page,
    sort_by: sortBy || undefined,
    order: order || undefined,
    name: searchName || undefined,
    postal_code: searchPostalCode || undefined,
  });

  const { mutate: downloadCsv, isPending: isDownloading } = useDownloadTownsCsv();
  const { importCsv, isImporting } = useImportTownsCsv();

  const listData = data?.data || [];
  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: listError,
    dataLength: listData.length,
  });

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleSearch = (filters: TownFilterParams) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });

    // Reset sort when searching
    params.delete('sort_by');
    params.delete('order');
    params.set('page', '1');
    setSearchParams(params);
    refetch();
  };

  const handleTableChange = (sorter: SorterResult<Town> | SorterResult<Town>[]) => {
    const params = new URLSearchParams(searchParams);
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (singleSorter.order) {
      params.set('sort_by', (singleSorter.field || singleSorter.columnKey) as string);
      params.set('order', singleSorter.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCsvDownload = () => {
    downloadCsv({
      sort_by: sortBy || undefined,
      order: order || undefined,
      name: searchName || undefined,
      postal_code: searchPostalCode || undefined,
    });
  };

  const handleCsvImport = (file: File) => {
    importCsv(file);
  };

  return (
    <AdminTownListTemplate
      data={listData}
      total={data?.meta.total || 0}
      current={data?.meta.current_page || 1}
      pageSize={data?.meta.per_page || 20}
      sortInfo={{
        columnKey: sortBy || undefined,
        order: order || undefined,
      }}
      onChange={handlePageChange}
      onTableChange={handleTableChange}
      onSearch={handleSearch}
      onCsvDownload={handleCsvDownload}
      isCsvDownloading={isDownloading}
      onCsvImport={handleCsvImport}
      isCsvImporting={isImporting}
      breadcrumbItems={breadcrumbItems}
      isLoading={isLoading}
    />
  );
};

export default SiteAdminTownPage;
